import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { getHrmsBranchList } from '../../../Redux/action/Organisation/Branch/BranchAction';
import { adminMainUrl } from '../../../Config';

import { getCustomerDetailbyUsername } from '../../../Redux/action/MasterAction';

/**
 * SendForPricingModal
 *
 * Props:
 *  - show                {boolean}  control modal visibility
 *  - handleClose         {fn}       close the modal
 *  - quotationId         {string|number}
 *  - pricingCompanyValues{object}   company values from SendPricing form
 *  - quoteDetailData     {object}   quote details from parent
 *  - quoteCompGenInfoData{object}   company general info from parent
 *  - refresh             {fn}       refresh parent data after success
 *  - sendMessageToParent {fn}       post-message to opener window
 */
const SendForPricingModal = ({
    show,
    handleClose,
    quotationId,
    pricingCompanyValues,
    quoteDetailData,
    quoteCompGenInfoData,
    refresh,
    sendMessageToParent,
    mode = 'pricing',
}) => {
    const isRepricing = mode === 'repricing';
    const dispatch = useDispatch();
    const token = Cookies.get('token');
    const user_id = Cookies.get('flexichain_user_id');

    // ── Redux ────────────────────────────────────────────────────────────
    const { sidebarPermissionHRMSData } = useSelector(state => state?.NavbarReducer);
    const { HrmsBranchList } = useSelector(state => state?.BranchReducer);
    const { CustomerDetailbyUsername } = useSelector(state => state?.MasterReducer || {});

    // Fetch customer profile details if quotation has customer username
    useEffect(() => {
        if (show && quoteDetailData?.customer_username) {
            dispatch(getCustomerDetailbyUsername(quoteDetailData.customer_username));
        }
    }, [show, quoteDetailData]);

    // Admin if role_id equals user_id (same pattern as PickedQueryList2)
    const isAdmin = sidebarPermissionHRMSData?.role_id == sidebarPermissionHRMSData?.user_id;

    console.log(sidebarPermissionHRMSData, 'this is sidebarPermissionHRMSData')

    // ── State ─────────────────────────────────────────────────────────────
    const [branchOptions, setBranchOptions] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // ── Fetch branch list on open ─────────────────────────────────────────
    useEffect(() => {
        if (show && token && user_id) {
            dispatch(getHrmsBranchList(token, user_id));
        }
    }, [show, token, user_id]);

    // ── Build branch options & auto-fill ──────────────────────────────────
    useEffect(() => {
        if (HrmsBranchList && Array.isArray(HrmsBranchList) && show) {
            const opts = HrmsBranchList.map(b => ({
                value: b.branch_id,
                label: b.branch_name,
            }));
            setBranchOptions(opts);

            // Auto-select branch:
            // 1. If repricing, check if the quotation already has an assigned branch
            // 2. Otherwise for non-admin, use current logged-in user's branch
            let targetBranch = null;
            if (isRepricing && (quoteDetailData?.assign_branch_id || quoteDetailData?.branch_id)) {
                const prevBranchId = quoteDetailData.assign_branch_id || quoteDetailData.branch_id;
                targetBranch = opts.find(o => String(o.value) === String(prevBranchId));
            }

            if (!targetBranch && !isAdmin && sidebarPermissionHRMSData && typeof sidebarPermissionHRMSData === 'object' && !Array.isArray(sidebarPermissionHRMSData)) {
                if (sidebarPermissionHRMSData.branch_id) {
                    targetBranch = opts.find(o => String(o.value) === String(sidebarPermissionHRMSData.branch_id));
                }
                if (!targetBranch) {
                    const branchName = (sidebarPermissionHRMSData.branch_name || sidebarPermissionHRMSData.branch || '').trim().toLowerCase();
                    if (branchName) {
                        targetBranch = opts.find(o => o.label?.trim().toLowerCase() === branchName);
                    }
                }
            }

            if (targetBranch) {
                setSelectedBranch(targetBranch);
                fetchUsersByBranch(targetBranch.value);
            }
        }
    }, [HrmsBranchList, isAdmin, sidebarPermissionHRMSData, show, isRepricing, quoteDetailData]);

    // ── Fetch users by branch ─────────────────────────────────────────────
    const fetchUsersByBranch = async (branch_id) => {
        if (!branch_id) return;
        setLoadingUsers(true);
        setSelectedUser(null);
        setUserOptions([]);
        try {
            const config = {
                headers: {
                    'auth-token': token,
                    'user_id': user_id,
                    'Content-Type': 'application/json',
                },
            };

            // Find Pricing module from sidebarPermissionHRMSData.modules
            const pricingModule = sidebarPermissionHRMSData?.modules?.find(
                (m) => m?.short_name?.toLowerCase() === 'pricing' || m?.name?.toLowerCase() === 'pricing'
            );
            const moduleId = pricingModule?.id || 3;

            const res = await axios.get(
                `${adminMainUrl}/api/employee-list?branch_id=${branch_id}&module_id=${moduleId}`,
                config
            );
            const data = res.data?.data;
            if (Array.isArray(data)) {
                const mapped = data.map(u => ({
                    value: u.user_id ?? u.id,
                    label: u.name,
                }));
                setUserOptions(mapped);

                // If repricing, auto-select previous pricing user if they belong to this branch
                if (isRepricing && (quoteDetailData?.pricing_user_id || quoteDetailData?.assign_user_id)) {
                    const prevUserId = quoteDetailData.pricing_user_id || quoteDetailData.assign_user_id;
                    const prevUser = mapped.find(u => String(u.value) === String(prevUserId));
                    if (prevUser) {
                        setSelectedUser(prevUser);
                    }
                }
            }
        } catch (err) {
            console.error('Error fetching users by branch:', err);
            toast.error('Failed to load users for the selected branch.');
        } finally {
            setLoadingUsers(false);
        }
    };

    // ── Handle branch change ──────────────────────────────────────────────
    const handleBranchChange = (option) => {
        setSelectedBranch(option);
        setSelectedUser(null);
        if (option) {
            fetchUsersByBranch(option.value);
        } else {
            setUserOptions([]);
        }
    };

    // ── Reset on close ────────────────────────────────────────────────────
    const handleModalClose = () => {
        if (isAdmin) {
            setSelectedBranch(null);
            setSelectedUser(null);
            setUserOptions([]);
        }
        handleClose();
    };

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const payload = {
                quotation_id: quotationId,
                com_company_name: pricingCompanyValues?.com_company_name || quoteCompGenInfoData?.com_company_name || quoteDetailData?.company_name || quoteDetailData?.full_name || "",
                com_address: pricingCompanyValues?.com_address || quoteCompGenInfoData?.com_address || CustomerDetailbyUsername?.permanentRegaddress || quoteDetailData?.pickup_address || quoteDetailData?.origin_address || quoteDetailData?.address || "",
                com_phone_no: pricingCompanyValues?.com_phone_no || quoteCompGenInfoData?.com_phone_no || quoteDetailData?.phone || "",
                com_email: pricingCompanyValues?.com_email || quoteCompGenInfoData?.com_email || quoteDetailData?.email || "",
                com_gstin: pricingCompanyValues?.com_gstin || quoteCompGenInfoData?.com_gstin || CustomerDetailbyUsername?.GST || quoteDetailData?.gstin || "",
                com_country_id: pricingCompanyValues?.com_country_id || quoteCompGenInfoData?.com_country_id || quoteDetailData?.origin_port_country_id || quoteDetailData?.origin_country_id || quoteDetailData?.country_id || "101",
                com_state_id: pricingCompanyValues?.com_state_id || quoteCompGenInfoData?.com_state_id || quoteDetailData?.origin_port_state_id || quoteDetailData?.origin_state_id || quoteDetailData?.state_id || "",
                ...(selectedBranch ? { assign_branch_id: selectedBranch.value, branch_id: selectedBranch.value } : {}),
                ...(selectedUser ? { assign_user_id: selectedUser.value, assigned_to: selectedUser.value, pricing_user_id: selectedUser.value } : {}),
            };

            const config = {
                headers: {
                    'auth-token': token,
                    'user_id': user_id,
                    'Content-Type': 'application/json',
                },
            };

            const endpoint = isRepricing
                ? `${adminMainUrl}/api/quotation/to_be_sent_to_repricing_with_gen_info`
                : `${adminMainUrl}/api/quotation/to_be_sent_to_pricing_with_gen_info`;

            const res = await axios.post(
                endpoint,
                payload,
                config
            );

            if (res.status === 200) {
                toast.success(res.data?.msg || (isRepricing ? 'Sent for repricing successfully.' : 'Sent for pricing successfully.'));
                handleModalClose();
                if (refresh) refresh();
                if (sendMessageToParent) sendMessageToParent();
            } else {
                toast.error(res.data?.msg || 'Something went wrong.');
            }
        } catch (err) {
            console.error('Error sending for pricing:', err);
            toast.error(err?.response?.data?.msg || (isRepricing ? 'Failed to send for repricing.' : 'Failed to send for pricing.'));
        } finally {
            setSubmitting(false);
        }
    };

    // ── Custom select styles ──────────────────────────────────────────────
    const selectStyles = {
        control: base => ({
            ...base,
            minHeight: 36,
            fontSize: '13px',
            borderRadius: '6px',
        }),
        // menuPortal must have a z-index above Bootstrap modal (1055) and its backdrop (1050)
        menuPortal: base => ({ ...base, zIndex: 10001 }),
        menu: base => ({ ...base, fontSize: '13px' }),
        option: base => ({ ...base, fontSize: '13px' }),
        singleValue: base => ({ ...base, fontSize: '13px' }),
        placeholder: base => ({ ...base, fontSize: '13px', color: '#aaa' }),
    };

    return (
        <Modal show={show} onHide={handleModalClose} centered size="md">
            <Modal.Header closeButton className="border-bottom pb-2">
                <Modal.Title style={{ fontSize: '16px', fontWeight: 600 }}>
                    {isRepricing ? (
                        <>
                            <i className="fa-solid fa-rotate me-2 text-warning" />
                            Send for Repricing
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-paper-plane me-2 text-primary" />
                            Send for Pricing
                        </>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-3">
                {(pricingCompanyValues || quoteCompGenInfoData || quoteDetailData) && (
                    <div className="mb-3 p-2 bg-light rounded border" style={{ fontSize: '12px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <span className="text-muted fw-semibold">Company</span>
                            <strong className="text-dark">
                                {pricingCompanyValues?.com_company_name || quoteCompGenInfoData?.com_company_name || quoteDetailData?.company_name || quoteDetailData?.full_name || "-"}
                            </strong>
                        </div>
                        {(pricingCompanyValues?.com_email || quoteCompGenInfoData?.com_email || quoteDetailData?.email) && (
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted">Email</span>
                                <span>{pricingCompanyValues?.com_email || quoteCompGenInfoData?.com_email || quoteDetailData?.email}</span>
                            </div>
                        )}
                        {(pricingCompanyValues?.com_phone_no || quoteCompGenInfoData?.com_phone_no || quoteDetailData?.phone) && (
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted">Phone</span>
                                <span>{pricingCompanyValues?.com_phone_no || quoteCompGenInfoData?.com_phone_no || quoteDetailData?.phone}</span>
                            </div>
                        )}
                        {(pricingCompanyValues?.com_address || quoteCompGenInfoData?.com_address || CustomerDetailbyUsername?.permanentRegaddress || quoteDetailData?.pickup_address || quoteDetailData?.origin_address) && (
                            <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="text-muted">Address</span>
                                <span className="text-truncate ms-2" style={{ maxWidth: '65%' }}>
                                    {pricingCompanyValues?.com_address || quoteCompGenInfoData?.com_address || CustomerDetailbyUsername?.permanentRegaddress || quoteDetailData?.pickup_address || quoteDetailData?.origin_address}
                                </span>
                            </div>
                        )}
                        {(pricingCompanyValues?.com_gstin || quoteCompGenInfoData?.com_gstin || CustomerDetailbyUsername?.GST) && (
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted">GSTIN</span>
                                <span>{pricingCompanyValues?.com_gstin || quoteCompGenInfoData?.com_gstin || CustomerDetailbyUsername?.GST}</span>
                            </div>
                        )}
                    </div>
                )}

                <p className="text-muted mb-3" style={{ fontSize: '13px' }}>
                    {isRepricing
                        ? 'Select or reassign the branch and pricing team member for repricing.'
                        : 'Optionally assign a branch and pricing team member before sending. All fields are optional — leave blank to send without assignment.'}
                </p>

                {/* Branch */}
                <div className="mb-3">
                    <label
                        htmlFor="pricing-branch"
                        className="form-label fw-semibold"
                        style={{ fontSize: '13px' }}
                    >
                        Branch
                        {!isAdmin && selectedBranch && (
                            <span
                                className="ms-2 badge bg-info text-white"
                                style={{ fontSize: '10px' }}
                            >
                                Auto-filled
                            </span>
                        )}
                    </label>
                    <Select
                        inputId="pricing-branch"
                        options={branchOptions}
                        value={selectedBranch}
                        onChange={handleBranchChange}
                        isClearable
                        isDisabled={false}
                        placeholder="Select branch (optional)"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                    {!isAdmin && selectedBranch && (
                        <small className="text-muted" style={{ fontSize: '11px' }}>
                            Your branch is pre-filled. You can change it if needed.
                        </small>
                    )}
                </div>

                {/* User */}
                <div className="mb-3">
                    <label
                        htmlFor="pricing-user"
                        className="form-label fw-semibold"
                        style={{ fontSize: '13px' }}
                    >
                        Assign To (Pricing Team Member)
                    </label>
                    <Select
                        inputId="pricing-user"
                        options={userOptions}
                        value={selectedUser}
                        onChange={opt => setSelectedUser(opt)}
                        isClearable
                        isLoading={loadingUsers}
                        isDisabled={!selectedBranch}
                        placeholder={
                            !selectedBranch
                                ? 'Select a branch first'
                                : 'Select pricing team member (optional)'
                        }
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                    {selectedBranch && userOptions.length === 0 && !loadingUsers && (
                        <small className="text-warning" style={{ fontSize: '11px' }}>
                            No users found for this branch.
                        </small>
                    )}
                </div>

                {/* Summary badge */}
                {(selectedBranch || selectedUser) && (
                    <div
                        className="alert alert-info py-2 px-3 mb-0"
                        style={{ fontSize: '12px', borderRadius: '8px' }}
                    >
                        <strong>Will assign to:</strong>{' '}
                        {selectedBranch && (
                            <span className="badge bg-primary me-1">{selectedBranch.label}</span>
                        )}
                        {selectedUser && (
                            <>
                                <i className="fa fa-arrow-right mx-1" style={{ fontSize: '10px' }} />
                                <span className="badge bg-success">{selectedUser.label}</span>
                            </>
                        )}
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top pt-2">
                <button
                    className="btn btn-sm btn-light"
                    onClick={handleModalClose}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    id="send-for-pricing-submit"
                    className="btn btn-sm btn-primary px-4"
                    onClick={handleSubmit}
                    disabled={submitting}
                >
                    {submitting ? (
                        <>
                            <i className="fa fa-spinner fa-spin me-1" />
                            Sending…
                        </>
                    ) : isRepricing ? (
                        <>
                            <i className="fa-solid fa-rotate me-1" />
                            {selectedUser ? `Send Repricing for ${selectedUser.label}` : 'Send Repricing to Team'}
                        </>
                    ) : (
                        <>
                            <i className="fa-solid fa-paper-plane me-1" />
                            {selectedUser ? `Send for ${selectedUser.label}` : 'Send Pricing Team'}
                        </>
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default SendForPricingModal;
